"use strict";
// The pricing maths — the STM002 Take Off and Summary sheets (Rev 41), in one
// place (docs/PRICING.md). Pure: rates and quantities in, figures out; nothing
// is rounded until it is shown. Each step names the template cells it copies.
//
//   surface sell   = hours × wage rate + litres × material rate          (row 48: K)
//   section hours  = surfaces (or approved) + sundries + wash + scaffold + down time (H102)
//   overtime, foreman, abseil = % of the section's hours × their rate     (rows 87–89)
//   access, other trades = qty × rate × (1 + mark-up other)               (rows 69–84)
//   fuel levy      = section hours × $/hr, no mark-up                     (row 77)
//   negotiating factor = the section's sell × factor                      (row 90)
// Summary: sell by cost code; cost = labour hours × cost rates, paint litres
// × cost, everything marked up at "other" ÷ (1 + mark-up other).
Object.defineProperty(exports, "__esModule", { value: true });
exports.surfaceWork = surfaceWork;
exports.priceSection = priceSection;
exports.priceOption = priceOption;
const job_prices_ts_1 = require("./job-prices.js");
const PRICE_CHECK_TOLERANCE = 0.005;
const MINUTES_PER_HOUR = 60;
const DAYS_PER_WEEK = 7;
/** Row 63's spread rate: sundry work uses 1 L of paint per hour (DD, 2026-10-09). */
const SUNDRY_HOURS_PER_LITRE = 1;
const sellOf = (paint, rates) => paint.costPerLitre * (1 + rates.markupMaterials);
function paintFor(surface, rates) {
    const paint = rates.paints.find((p) => p.id === surface.paintId) ?? rates.paints[0];
    if (!paint)
        throw new Error('No paint rates — load the pricing settings first.');
    return paint;
}
/** Row 48's hours and litres for one surface (or paint system), before any money. An unpriced (wash-only) row has none. */
function surfaceWork(surface) {
    if (surface.unpriced)
        return { hours: 0, prepHours: 0, litres: 0 };
    const prepHours = surface.prep > 0 ? surface.quantity / surface.prep : 0;
    const paintHours = surface.rate > 0 ? (surface.quantity * surface.coats) / surface.rate : 0;
    const hours = (paintHours + prepHours) * (1 + (surface.uplift ?? 0));
    const litreGirth = surface.pitchInQuantity ? 1 : surface.girth;
    const litres = surface.spread > 0 ? (surface.quantity * surface.coats * litreGirth) / surface.spread : 0;
    return { hours, prepHours, litres };
}
/** Row 48 for one surface. */
function priceSurface(surface, rates) {
    const paint = paintFor(surface, rates);
    const materialRate = sellOf(paint, rates);
    const wageRate = rates.labour.sell + rates.allowance.sell;
    const { hours, prepHours, litres } = surfaceWork(surface);
    const sell = hours * wageRate + litres * materialRate;
    const area = surface.quantity * surface.girth;
    return { id: surface.id, name: surface.name, materialRate, wageRate, hours, prepHours, litres, sell, m2Rate: area > 0 ? sell / area : 0, paintId: paint.id };
}
function accessSell(line, markup) {
    const out = [
        { item: line.item, kind: 'Day Hire', units: line.days, sell: line.days * line.dayRate * markup },
        { item: line.item, kind: 'Cartage', units: line.trips, sell: line.trips * line.transport * markup },
    ];
    if (line.fuelPerWeek > 0) {
        const weeks = line.days / DAYS_PER_WEEK;
        out.push({ item: line.item, kind: 'Fuel', units: weeks, sell: weeks * line.fuelPerWeek * markup });
    }
    return out;
}
/** Every quantity in a section times the building's count; rates and percentages stay as they are. */
function countedSection(section) {
    const count = Math.max(1, section.buildingCount ?? 1);
    if (count === 1)
        return section;
    return {
        ...section,
        surfaces: section.surfaces.map((s) => ({ ...s, quantity: s.quantity * count })),
        approvedHours: typeof section.approvedHours === 'number' ? section.approvedHours * count : section.approvedHours,
        sundries: section.sundries?.map((s) => ({ ...s, items: s.items * count })),
        washArea: section.washArea === undefined ? undefined : section.washArea * count,
        scaffoldMoves: section.scaffoldMoves && { ...section.scaffoldMoves, count: section.scaffoldMoves.count * count },
        access: section.access?.map((a) => ({ ...a, days: a.days * count, trips: a.trips * count })),
        otherTrades: section.otherTrades?.map((o) => ({ ...o, qty: o.qty * count })),
    };
}
/** One section's block of rows (48–90) and its total. */
function priceSection(input, rates) {
    const section = countedSection(input);
    const wageRate = rates.labour.sell + rates.allowance.sell;
    const markupOther = 1 + rates.markupOther;
    const surfaces = section.surfaces.map((s) => priceSurface(s, rates));
    const measuredHours = surfaces.reduce((sum, s) => sum + s.hours, 0);
    const approved = section.approvedHours;
    const approvedAdjustmentHours = typeof approved === 'number' ? approved - measuredHours : 0;
    const surfaceHours = measuredHours + approvedAdjustmentHours;
    const standardPaint = rates.paints[0];
    const sundryMaterialRate = standardPaint ? sellOf(standardPaint, rates) : 0;
    const sundries = (section.sundries ?? []).map((line) => {
        const lineHours = (line.items * line.minutes) / MINUTES_PER_HOUR;
        // A wash-only area uses no paint (#510): its sundries are their labour only.
        const lineLitres = section.washOnly ? 0 : lineHours / (line.spread || SUNDRY_HOURS_PER_LITRE);
        return { description: line.description, hours: lineHours, litres: lineLitres, sell: lineHours * wageRate + lineLitres * sundryMaterialRate };
    });
    const sundryHours = sundries.reduce((sum, s) => sum + s.hours, 0);
    const sundryLitres = sundries.reduce((sum, s) => sum + s.litres, 0);
    const litres = surfaces.reduce((sum, s) => sum + s.litres, 0) + sundryLitres;
    const washRate = section.washRate ?? rates.washRate;
    const washHours = washRate > 0 ? (section.washArea ?? 0) / washRate : 0;
    const moves = section.scaffoldMoves;
    const scaffoldHours = moves ? (moves.count * moves.minutes) / MINUTES_PER_HOUR : 0;
    const beforeDowntime = surfaceHours + sundryHours + washHours + scaffoldHours;
    const downtimeHours = (section.downtime ?? 0) * beforeDowntime;
    const hours = beforeDowntime + downtimeHours;
    const consumables = litres * (section.consumablesRate ?? rates.consumablesPerLitre);
    const access = (section.access ?? []).flatMap((line) => accessSell(line, markupOther));
    const fuelLevy = hours * rates.fuelLevyPerHour;
    const otherTrades = (section.otherTrades ?? []).map((o) => ({ description: o.description, sell: o.qty * o.rate * markupOther }));
    const overtimeHours = (section.overtime ?? 0) * hours;
    const foremanHours = (section.foreman ?? 0) * hours;
    const abseilHours = (section.abseil ?? 0) * hours;
    // Surfaces carry their own labour and paint (row 48: K); the extra labour rows are at the wage rate.
    const beforeFactor = surfaces.reduce((sum, s) => sum + s.sell, 0) +
        (approvedAdjustmentHours + sundryHours + washHours + scaffoldHours + downtimeHours) * wageRate +
        sundryLitres * sundryMaterialRate +
        consumables +
        access.reduce((sum, a) => sum + a.sell, 0) +
        fuelLevy +
        otherTrades.reduce((sum, o) => sum + o.sell, 0) +
        overtimeHours * rates.overtime.sell +
        foremanHours * rates.foreman.sell +
        abseilHours * rates.abseil.sell;
    const negotiating = beforeFactor * rates.negotiatingFactor;
    return {
        id: section.id,
        name: section.name,
        surfaces,
        // A paint system shares its surface's measurement, so only the surfaces themselves make the area.
        area: section.surfaces.filter((s) => !s.systemOf).reduce((sum, s) => sum + s.quantity * s.girth, 0),
        downtimeHours,
        approvedAdjustmentHours,
        sundries,
        sundryHours,
        sundryLitres,
        washHours,
        scaffoldHours,
        hours,
        litres,
        overtimeHours,
        foremanHours,
        abseilHours,
        consumables,
        access,
        fuelLevy,
        otherTrades,
        overtimeSell: overtimeHours * rates.overtime.sell,
        foremanSell: foremanHours * rates.foreman.sell,
        abseilSell: abseilHours * rates.abseil.sell,
        negotiating,
        sell: beforeFactor + negotiating,
    };
}
const sum = (items, value) => items.reduce((total, item) => total + value(item), 0);
/** The Summary's Repaint Breakdown (B56:M107), one line per category. */
function breakdownOf(sections, rates) {
    const hours = sum(sections, (s) => s.hours);
    const markupOther = 1 + rates.markupOther;
    const lines = [
        { category: 'labour', label: 'Labour', quantity: hours, sell: hours * rates.labour.sell, cost: hours * rates.labour.cost },
    ];
    const share = (category, label, h, rate) => lines.push({ category, label, quantity: h, sell: h * rate.sell, cost: h * rate.cost });
    share('overtime', 'Overtime hours', sum(sections, (s) => s.overtimeHours), rates.overtime);
    share('foreman', 'Foreman', sum(sections, (s) => s.foremanHours), rates.foreman);
    share('abseil', 'Abseil hours', sum(sections, (s) => s.abseilHours), rates.abseil);
    share('allowance', 'Allowance', hours, rates.allowance);
    for (const [index, paint] of rates.paints.entries()) {
        // Sundries use the standard (first) paint, as row 63's material rate.
        const sundries = index === 0 ? sum(sections, (s) => s.sundryLitres) : 0;
        const litres = sundries + sum(sections, (s) => sum(s.surfaces.filter((x) => x.paintId === paint.id), (x) => x.litres));
        if (litres > 0)
            lines.push({ category: 'paint', label: (0, job_prices_ts_1.paintLabel)(paint), quantity: litres, sell: litres * sellOf(paint, rates), cost: litres * paint.costPerLitre });
    }
    const consumables = sum(sections, (s) => s.consumables);
    lines.push({ category: 'consumables', label: 'Consumables', quantity: null, sell: consumables, cost: consumables / markupOther });
    const hire = sum(sections, (s) => sum(s.access, (a) => a.sell));
    lines.push({ category: 'hire', label: 'Hire/Access Equipment', quantity: null, sell: hire, cost: hire / markupOther });
    const levy = sum(sections, (s) => s.fuelLevy);
    lines.push({ category: 'fuelLevy', label: 'Fuel Levy', quantity: null, sell: levy, cost: levy });
    const other = sum(sections, (s) => sum(s.otherTrades, (o) => o.sell));
    lines.push({ category: 'otherTrades', label: 'Other Trades/Permits/Material', quantity: null, sell: other, cost: other / markupOther });
    return lines;
}
/** An option priced in full: the Take Off's sections and the Summary. */
function priceOption(sections, rates) {
    const priced = sections.map((s) => priceSection(s, rates));
    const breakdown = breakdownOf(priced, rates);
    const sell = sum(priced, (s) => s.sell);
    const categorySell = sum(breakdown, (b) => b.sell);
    const contractAdjustment = sell - categorySell;
    const negotiating = sum(priced, (s) => s.negotiating);
    const cost = sum(breakdown, (b) => b.cost);
    return {
        sections: priced,
        breakdown,
        contractAdjustment,
        sell,
        cost,
        gp: sell > 0 ? (sell - cost) / sell : 0,
        hours: sum(priced, (s) => s.hours),
        litres: sum(priced, (s) => s.litres),
        priceCheckOk: Math.abs(contractAdjustment - negotiating) < PRICE_CHECK_TOLERANCE,
    };
}
